import {
  SalesRepGrossProfitValidationErrorCode,
  SalesRepPriceValidationErrorCode,
} from '@platform/errors';
import { useCallback } from 'react';
import { useUpdateSalesRepPriceOverrideMutation } from '@/domain/pricing/blocs/documents/update-sales-rep-price.generated';

interface CommitPriceParams {
  deliveryDate: string;
  expirationDate: string | null;
  priceInMicros: number | null;
  restaurantUUID: string;
  supplierUUID: string;
  variantPackUUID: string;
}

export interface PriceOverrideResult {
  defaultPriceInMicros: number | null;
  priceInMicros: number | null;
  salesRepGrossProfitInPercentage: number | null;
  salesRepPriceInMicros: number | null;
  unitCount: number;
  updatedByName: string | null;
}

/**
 * Mutator hook for saving a price override via `updateSalesRepPrice`.
 *
 * @param options.onError - Optional callback invoked on mutation failure
 * @returns `commitPrice(params)` — resolves to mutation response data or null on failure
 *
 * @example
 * ```typescript
 * const { commitPrice } = usePriceOverrideMutator({
 *   onError: () => showErrorToast(),
 * });
 *
 * const result = await commitPrice({
 *   deliveryDate: '2026-03-18',
 *   priceInMicros: 1500000,
 *   restaurantUUID: 'rest-123',
 *   supplierUUID: 'sup-456',
 *   variantPackUUID: 'vp-789',
 * });
 * ```
 */
export const usePriceOverrideMutator = ({
  onError,
}: { onError?: () => void } = {}) => {
  const [updateSalesRepPrice] = useUpdateSalesRepPriceOverrideMutation({
    context: {
      ignoreGraphQLErrorCodes: [
        SalesRepGrossProfitValidationErrorCode.SALES_REP_GROSS_PROFIT_TOO_HIGH,
        SalesRepPriceValidationErrorCode.SALES_REP_PRICE_TOO_LOW,
        SalesRepPriceValidationErrorCode.SALES_REP_PRICE_TOO_HIGH,
      ],
    },
  });

  const commitPrice = useCallback(
    async (params: CommitPriceParams): Promise<PriceOverrideResult | null> => {
      try {
        const { data } = await updateSalesRepPrice({
          variables: {
            deliveryDate: params.deliveryDate,
            expirationDate: params.expirationDate,
            restaurantUUID: params.restaurantUUID,
            supplierUUID: params.supplierUUID,
            variantPacks: [
              {
                sales_rep_price_in_micros: params.priceInMicros,
                variant_pack_id: params.variantPackUUID,
              },
            ],
          },
        });
        const result = data?.updateSalesRepPrice?.[0];
        return result
          ? {
              defaultPriceInMicros: result.default_price_in_micros ?? null,
              priceInMicros: result.price_in_micros ?? null,
              salesRepGrossProfitInPercentage:
                result.sales_rep_gross_profit_in_percentage ?? null,
              salesRepPriceInMicros: result.sales_rep_price_in_micros ?? null,
              unitCount: result.unit_count,
              updatedByName: result.sales_rep_metadata?.updated_by_name ?? null,
            }
          : null;
      } catch {
        onError?.();
        return null;
      }
    },
    [onError, updateSalesRepPrice],
  );

  return { commitPrice };
};
